import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { fmtDate } from "@/lib/format";
import { FlaskConical, CheckCircle2, XCircle, AlertTriangle, MinusCircle, ArrowLeft, Terminal, ChevronDown, ChevronRight, ShieldCheck, CircleSlash, RefreshCw, Loader2, StopCircle, Save, CalendarClock, TrendingDown, TrendingUp } from "lucide-react";

const VERDICT = {
  pass: "text-emerald-700 bg-emerald-50 border-emerald-200",
  warn: "text-amber-700 bg-amber-50 border-amber-200",
  fail: "text-red-700 bg-red-50 border-red-200",
  error: "text-zinc-600 bg-zinc-100 border-zinc-200",
};
const BAR = { pass: "bg-emerald-400 hover:bg-emerald-500", warn: "bg-amber-400 hover:bg-amber-500", fail: "bg-red-400 hover:bg-red-500", error: "bg-zinc-300 hover:bg-zinc-400" };

function StatusIcon({ status }) {
  if (status === "PASS") return <CheckCircle2 size={14} className="text-emerald-600" />;
  if (status === "FAIL") return <XCircle size={14} className="text-red-600" />;
  if (status === "WARN") return <AlertTriangle size={14} className="text-amber-600" />;
  return <MinusCircle size={14} className="text-zinc-400" />;
}

// Segmented pass/warn/fail/skip bar with a summary label — used on live + finished views.
function ResultBar({ counts }) {
  const { t } = useLang();
  const c = counts || {};
  const total = (c.PASS || 0) + (c.FAIL || 0) + (c.WARN || 0) + (c.SKIP || 0);
  if (!total) return null;
  const seg = (n, cls) => (n ? <div className={cls} style={{ width: `${(n / total) * 100}%` }} /> : null);
  return (
    <div data-testid="e2e-result-bar">
      <div className="flex h-2.5 w-full overflow-hidden rounded-sm bg-zinc-100">
        {seg(c.PASS, "bg-emerald-500")}
        {seg(c.WARN, "bg-amber-400")}
        {seg(c.FAIL, "bg-red-500")}
        {seg(c.SKIP, "bg-zinc-300")}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-zinc-500">
        <span><span className="font-bold text-emerald-600">{c.PASS || 0}</span> {t("e2e.pass")}</span>
        <span><span className={c.WARN ? "font-bold text-amber-600" : ""}>{c.WARN || 0}</span> {t("e2e.warn")}</span>
        <span><span className={c.FAIL ? "font-bold text-red-600" : ""}>{c.FAIL || 0}</span> {t("e2e.fail")}</span>
        {c.SKIP ? <span>{c.SKIP} SKIP</span> : null}
      </div>
    </div>
  );
}

// Progress bar: determinate when we know the expected total, otherwise an animated stripe.
function ProgressBar({ pct }) {
  if (pct == null)
    return (
      <div className="h-2 w-full overflow-hidden rounded-sm bg-zinc-100" data-testid="e2e-progress-indeterminate">
        <div className="h-full w-1/3 animate-[e2eslide_1.2s_ease-in-out_infinite] rounded-sm bg-primary" />
      </div>
    );
  return (
    <div className="h-2 w-full overflow-hidden rounded-sm bg-zinc-100">
      <div className="h-full rounded-sm bg-primary transition-all duration-500" style={{ width: `${pct}%` }} data-testid="e2e-progress-fill" />
    </div>
  );
}

// Flat, scrollable checklist that fills in as checks complete (live) or shows a whole run.
function CheckList({ results }) {
  return (
    <div className="max-h-80 space-y-0.5 overflow-auto pr-1" data-testid="e2e-checklist">
      {results.map((r, i) => (
        <div key={i} data-testid="e2e-check-row" className="flex items-start gap-2 py-1 text-sm">
          <span className="mt-0.5 shrink-0"><StatusIcon status={r.status} /></span>
          <div className="min-w-0 flex-1">
            <span className="text-zinc-800">{r.name}</span>
            {r.mode && r.mode !== "-" && <span className="ml-1.5 rounded-sm bg-zinc-100 px-1 py-0.5 text-[10px] font-bold uppercase text-zinc-500">{r.mode}</span>}
            {r.detail && <span className="ml-2 font-mono text-[11px] text-zinc-400">{r.detail}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// Health summary + on-demand "Run now" with a realtime progress view.
function HealthPanel({ onCompleted }) {
  const { t } = useLang();
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(null);
  const ivRef = useRef(null);
  const load = () => api.get("/e2e/health").then((r) => setH(r.data)).catch(() => setH({ enabled: false }));

  const startPolling = () => {
    if (ivRef.current) clearInterval(ivRef.current);
    let tries = 0;
    ivRef.current = setInterval(async () => {
      tries += 1;
      const p = await api.get("/e2e/progress").then((x) => x.data).catch(() => null);
      if (p) setProg(p);
      if (p && p.done && !p.running) {
        clearInterval(ivRef.current); ivRef.current = null; setBusy(false); load(); onCompleted && onCompleted();
      } else if (tries >= 400) { clearInterval(ivRef.current); ivRef.current = null; setBusy(false); }
    }, 1500);
  };

  useEffect(() => {
    load();
    // Resume a run that is ALREADY in progress (e.g. started before navigating here / on another tab).
    api.get("/e2e/progress").then((r) => {
      if (r.data?.running) { setBusy(true); setProg(r.data); startPolling(); }
    }).catch(() => {});
    return () => { if (ivRef.current) clearInterval(ivRef.current); };
  }, []);
  if (!h) return null;

  const last = h.last;
  const verdict = last?.verdict || "none";
  const map = {
    pass: [VERDICT.pass, ShieldCheck, t("e2e.pass")],
    warn: [VERDICT.warn, AlertTriangle, t("e2e.warn")],
    fail: [VERDICT.fail, AlertTriangle, t("e2e.fail")],
    error: [VERDICT.error, CircleSlash, t("e2e.error")],
    none: [VERDICT.error, FlaskConical, t("e2e.never")],
  };
  const [cls, Icon, lbl] = map[verdict] || map.none;
  const c = last?.counts || {};
  const total = (c.PASS || 0) + (c.FAIL || 0) + (c.WARN || 0) + (c.SKIP || 0);

  const runNow = async () => {
    setBusy(true);
    setProg({ running: true, completed: 0, expectedTotal: 0, results: [], counts: {} });
    try {
      await api.post("/e2e/run");
      toast.success(t("e2e.started"));
      startPolling();
    } catch (e) { toast.error(apiErr(e)); setBusy(false); setProg(null); }
  };

  const cancel = async () => {
    try {
      await api.post("/e2e/cancel");
      toast.message(t("e2e.cancelledToast"));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const isRunning = busy || !!prog?.running;

  const live = busy && prog;
  const done = prog?.completed || 0;
  const exp = prog?.expectedTotal || 0;
  const pct = prog?.done ? 100 : (exp > 0 ? Math.min(99, Math.round((done / exp) * 100)) : null);

  return (
    <div data-testid="e2e-health-panel" className="mb-6 border border-zinc-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-600">
            <FlaskConical size={14} /> {live ? t("e2e.liveTitle") : t("e2e.latestRun")}
            {!live && <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10px] font-bold ${cls}`} data-testid="e2e-panel-verdict"><Icon size={11} /> {lbl}</span>}
          </div>

          {live ? (
            <div className="mt-3" data-testid="e2e-live">
              <div className="mb-1 flex items-center justify-between text-xs text-zinc-500">
                <span data-testid="e2e-live-count">{done}{exp > 0 ? ` / ~${exp}` : ""} {t("e2e.checksLabel")}</span>
                <span>{pct == null ? "" : `${pct}%`}</span>
              </div>
              <ProgressBar pct={pct} />
              <div className="mt-2"><ResultBar counts={prog.counts} /></div>
              <div className="mt-3 border-t border-zinc-100 pt-2"><CheckList results={prog.results || []} /></div>
            </div>
          ) : last ? (
            <>
              <div className="mt-3 flex items-end gap-2">
                <span className="font-display text-4xl font-black tracking-tighter text-zinc-950" data-testid="e2e-panel-passcount">{c.PASS || 0}<span className="text-2xl text-zinc-300">/{total}</span></span>
                <span className="mb-1 text-xs text-zinc-400">{t("e2e.checksPassed")}</span>
              </div>
              <div className="mt-2 max-w-md"><ResultBar counts={c} /></div>
              <div className="mt-2 flex items-center gap-1.5 font-mono text-[11px] text-zinc-400" data-testid="e2e-panel-lastrun">
                {h.suite && <span className="rounded-sm bg-zinc-100 px-1.5 py-0.5 font-bold uppercase tracking-wider text-zinc-500">{h.suite}</span>}
                {fmtDate(last.ranAt)}
              </div>
              {h.history && h.history.length > 1 && (
                <div className="mt-3 flex items-end gap-1" data-testid="e2e-panel-sparkline">
                  {[...h.history].reverse().map((x, i) => {
                    const xc = x.counts || {};
                    const xt = (xc.PASS || 0) + (xc.FAIL || 0) + (xc.WARN || 0) + (xc.SKIP || 0);
                    return <span key={i} title={`${xc.PASS || 0}/${xt} · ${fmtDate(x.ranAt)}`} className={`h-6 w-2.5 rounded-sm ${BAR[x.verdict] || BAR.error}`} data-testid="e2e-panel-bar" />;
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="mt-3 text-sm text-zinc-400" data-testid="e2e-panel-empty">{t("e2e.never")}</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <button
            onClick={runNow}
            disabled={isRunning || !h.enabled}
            data-testid="e2e-panel-runnow"
            title={!h.enabled ? t("e2e.runDisabledHint") : ""}
            className="inline-flex items-center gap-1.5 border border-zinc-300 bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#002480] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isRunning ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} {isRunning ? t("e2e.running") : t("e2e.runNow")}
          </button>
          {isRunning && (
            <button onClick={cancel} data-testid="e2e-panel-cancel"
              className="inline-flex items-center gap-1.5 border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-50">
              <StopCircle size={15} /> {t("e2e.cancel")}
            </button>
          )}
          {!h.enabled && <span className="max-w-[220px] text-right text-[11px] leading-snug text-zinc-400">{t("e2e.runDisabledHint")}</span>}
        </div>
      </div>
    </div>
  );
}

// Admin editor for the nightly schedule (suite + run hour). Only shown on TEST_MODE instances.
function SchedulePanel() {
  const { t } = useLang();
  const [cfg, setCfg] = useState(null);
  const [suite, setSuite] = useState("");
  const [hour, setHour] = useState(0);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/e2e/config").then((r) => {
      setCfg(r.data); setSuite(r.data.suite || ""); setHour(r.data.runHour ?? 0);
    }).catch(() => setCfg({ enabled: false }));
  }, []);
  if (!cfg || !cfg.enabled) return null;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/e2e/config", { suite, runHour: Number(hour) });
      setCfg((p) => ({ ...p, suite: r.data.suite, runHour: r.data.runHour }));
      toast.success(t("e2e.scheduleSaved"));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mb-6 border border-zinc-200 bg-white p-5" data-testid="e2e-schedule-panel">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-600">
        <CalendarClock size={14} /> {t("e2e.schedule")}
      </div>
      <p className="mt-1 text-sm text-zinc-500">{t("e2e.scheduleDesc")}</p>
      <div className="mt-4 flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-500">
          {t("e2e.suite")}
          <select value={suite} onChange={(e) => setSuite(e.target.value)} data-testid="e2e-schedule-suite"
            className="min-w-[160px] border border-zinc-300 bg-white px-3 py-2 text-sm font-normal text-zinc-900">
            {(cfg.suites || []).map((s) => <option key={s} value={s}>{s}{s === cfg.envSuite ? " (default)" : ""}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-500">
          {t("e2e.runHour")}
          <input type="number" min={0} max={23} value={hour}
            onChange={(e) => setHour(e.target.value)} data-testid="e2e-schedule-hour"
            className="w-28 border border-zinc-300 bg-white px-3 py-2 text-sm font-normal text-zinc-900" />
        </label>
        <button onClick={save} disabled={saving} data-testid="e2e-schedule-save"
          className="inline-flex items-center gap-1.5 border border-zinc-300 bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#002480] disabled:opacity-50">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {t("common.save")}
        </button>
      </div>
      <div className="mt-3 text-[11px] text-zinc-400" data-testid="e2e-schedule-hint">
        {t("e2e.scheduleHint").replace("{h}", String(cfg.runHour ?? hour).padStart(2, "0")).replace("{suite}", cfg.suite || cfg.envSuite || "—")}
        <div className="mt-1">{t("e2e.suiteSafeHint")}</div>
      </div>
    </div>
  );
}

// Regression diff box: checks that newly broke (and optionally recovered) vs the previous run.
function RegressionBox({ regression }) {
  const { t } = useLang();
  const nf = regression?.newFailures || [];
  const rec = regression?.recovered || [];
  if (!nf.length && !rec.length) return null;
  return (
    <div className="px-5 py-4" data-testid="e2e-regression-box">
      {nf.length > 0 && (
        <div className="mb-3" data-testid="e2e-regression-new">
          <div className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-600">
            <TrendingDown size={14} /> {t("e2e.regressions")} · {nf.length}
          </div>
          <div className="space-y-1">
            {nf.map((r, i) => (
              <div key={i} className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 px-2.5 py-1.5 text-sm" data-testid="e2e-regression-row">
                <XCircle size={14} className="mt-0.5 shrink-0 text-red-600" />
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-red-800">{r.name}</span>
                  <span className="ml-1.5 rounded-sm bg-red-100 px-1 py-0.5 text-[10px] font-bold uppercase text-red-500">{t("e2e.wasStatus").replace("{s}", r.from)}</span>
                  {r.detail && <span className="ml-2 font-mono text-[11px] text-red-500">{r.detail}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {rec.length > 0 && (
        <div data-testid="e2e-regression-recovered">
          <div className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-600">
            <TrendingUp size={14} /> {t("e2e.recovered")} · {rec.length}
          </div>
          <div className="space-y-1">
            {rec.map((r, i) => (
              <div key={i} className="flex items-start gap-2 py-0.5 text-sm">
                <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                <span className="text-zinc-700">{r.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function E2EHistory() {
  const { t } = useLang();
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(0);
  const [showLog, setShowLog] = useState(false);

  const reload = () => api.get("/e2e/runs?limit=30").then((r) => { setD(r.data); setSel(0); }).catch(() => setD({ enabled: false, runs: [] }));
  useEffect(() => { reload(); }, []);
  useEffect(() => { setShowLog(false); }, [sel]);

  if (!d)
    return (
      <>
        <PageHeader title={t("e2e.historyTitle")} subtitle={t("e2e.historySubtitle")} />
        <div className="p-10 font-mono text-xs uppercase tracking-widest text-zinc-400">{t("common.loading")}</div>
      </>
    );

  const runs = d.runs || [];
  const run = runs[sel];
  const results = run?.results || [];
  const sections = [];
  results.forEach((r) => { if (!sections.includes(r.section)) sections.push(r.section); });

  return (
    <>
      <PageHeader title={t("e2e.historyTitle")} subtitle={t("e2e.historySubtitle")} />
      <div className="mb-4">
        <Link to="/" data-testid="e2e-history-back" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
          <ArrowLeft size={14} /> {t("e2e.back")}
        </Link>
      </div>

      <HealthPanel onCompleted={reload} />

      <SchedulePanel />

      {!d.enabled && (
        <div data-testid="e2e-history-disabled" className="mb-6 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          {t("e2e.disabledNote")}
        </div>
      )}

      {runs.length === 0 ? (
        <div data-testid="e2e-history-empty" className="border border-zinc-200 bg-white p-10 text-center text-sm text-zinc-400">
          {t("e2e.never")}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Run list */}
          <div className="space-y-2 lg:col-span-1" data-testid="e2e-history-list">
            {runs.map((r, i) => {
              const c = r.counts || {};
              const active = i === sel;
              return (
                <button
                  key={i}
                  onClick={() => setSel(i)}
                  data-testid={`e2e-run-item-${i}`}
                  className={`flex w-full items-center justify-between border px-4 py-3 text-left transition-colors ${active ? "border-primary bg-primary/5" : "border-zinc-200 bg-white hover:bg-zinc-50"}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase ${VERDICT[r.verdict] || VERDICT.error}`}>{t(`e2e.${r.verdict}`)}</span>
                      <span className="rounded-sm bg-zinc-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500">{r.suite}</span>
                    </div>
                    <div className="mt-1 font-mono text-[11px] text-zinc-500">{fmtDate(r.ranAt)}</div>
                  </div>
                  <div className="shrink-0 text-right text-xs">
                    <span className="font-bold text-emerald-600">{c.PASS || 0}</span>
                    <span className="text-zinc-300"> / </span>
                    <span className={c.FAIL ? "font-bold text-red-600" : "text-zinc-400"}>{c.FAIL || 0}</span>
                    <span className={c.WARN ? "font-bold text-amber-600" : "text-zinc-400"}> · {c.WARN || 0}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Selected run detail */}
          <div className="lg:col-span-2" data-testid="e2e-history-detail">
            {run && (
              <div className="border border-zinc-200 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-200 bg-zinc-50 px-5 py-3">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-600">
                    <FlaskConical size={14} /> {run.suite} · {fmtDate(run.ranAt)}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                    <span>{run.source === "manual" ? t("e2e.manual") : t("e2e.scheduled")}</span>
                    {run.full && <span className="rounded-sm bg-zinc-200 px-1.5 py-0.5 font-bold uppercase">FULL</span>}
                  </div>
                </div>
                {run.counts && (
                  <div className="border-b border-zinc-100 px-5 py-3" data-testid="e2e-detail-summary">
                    <ResultBar counts={run.counts} />
                  </div>
                )}
                <div className="divide-y divide-zinc-100">
                  <RegressionBox regression={run.regression} />
                  {run.error && (
                    <div className="px-5 py-4" data-testid="e2e-run-error">
                      <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-600">
                        <AlertTriangle size={14} /> {t("e2e.errorReason")}
                      </div>
                      <p className="text-sm text-red-800">{run.error}</p>
                    </div>
                  )}
                  {run.log && (
                    <div className="px-5 py-3">
                      <button
                        onClick={() => setShowLog((v) => !v)}
                        data-testid="e2e-log-toggle"
                        className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-800"
                      >
                        {showLog ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <Terminal size={13} /> {showLog ? t("e2e.hideLog") : t("e2e.showLog")}
                      </button>
                      {showLog && (
                        <pre data-testid="e2e-run-log" className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap rounded-sm bg-zinc-950 p-4 font-mono text-[11px] leading-relaxed text-zinc-100">
{run.log}
                        </pre>
                      )}
                    </div>
                  )}
                  {sections.length === 0 && !run.error && !run.log && (
                    <div className="px-5 py-8 text-center text-sm text-zinc-400">{t("e2e.noDetail")}</div>
                  )}
                  {sections.map((sec) => (
                    <div key={sec} className="px-5 py-3">
                      <div className="mb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">{sec}</div>
                      <div className="space-y-1">
                        {results.filter((r) => r.section === sec).map((r, j) => (
                          <div key={j} data-testid="e2e-detail-row" className="flex items-start gap-2 py-1 text-sm">
                            <span className="mt-0.5 shrink-0"><StatusIcon status={r.status} /></span>
                            <div className="min-w-0 flex-1">
                              <span className="text-zinc-800">{r.name}</span>
                              {r.mode && r.mode !== "-" && <span className="ml-1.5 rounded-sm bg-zinc-100 px-1 py-0.5 text-[10px] font-bold uppercase text-zinc-500">{r.mode}</span>}
                              {r.detail && <span className="ml-2 font-mono text-[11px] text-zinc-400">{r.detail}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
